import React, { useState } from 'react';
import { ColorPicker, Radio, Button, Modal, Space, Typography, Row, Col, Card } from 'antd';
import { BgColorsOutlined, BorderOutlined } from '@ant-design/icons';
import GradientTemplateSelector from './GradientTemplateSelector';
import { GradientTemplate } from '../../data/gradientTemplates';

const { Text } = Typography;

interface ColorGradientSelectorProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  tooltip?: string;
  showText?: boolean;
  style?: React.CSSProperties;
}

const ColorGradientSelector: React.FC<ColorGradientSelectorProps> = ({
  value = '',
  onChange,
  label,
  tooltip,
  showText = false,
  style = { width: '100%' }
}) => {
  const [colorType, setColorType] = useState<'solid' | 'gradient'>('solid');
  const [gradientModalVisible, setGradientModalVisible] = useState(false);

  // 判断当前值是否为渐变色
  const isGradient = value && value.includes('gradient');
  
  // 初始化颜色类型
  React.useEffect(() => {
    if (isGradient) {
      setColorType('gradient');
    } else {
      setColorType('solid');
    }
  }, [isGradient]);

  const handleColorTypeChange = (e: any) => {
    const newType = e.target.value;
    setColorType(newType);
    
    if (newType === 'solid' && isGradient) {
      // 从渐变色切换到单色，提取第一个颜色
      const colorMatch = value.match(/#[0-9a-fA-F]{6}/);
      const solidColor = colorMatch ? colorMatch[0] : '#1890ff';
      onChange?.(solidColor);
    } else if (newType === 'gradient' && !isGradient) {
      // 从单色切换到渐变色，创建简单的渐变色
      const gradient = `linear-gradient(135deg, ${value || '#1890ff'} 0%, ${value || '#1890ff'} 100%)`;
      onChange?.(gradient);
    }
  };

  const handleSolidColorChange = (color: any) => {
    const hexColor = color.toHexString();
    onChange?.(hexColor);
  };

  const handleGradientSelect = (template: GradientTemplate) => {
    onChange?.(template.gradient);
    setGradientModalVisible(false);
  };

  const handleGradientModalOpen = () => {
    setGradientModalVisible(true);
  };

  const renderPreview = () => {
    if (colorType === 'gradient' && value) {
      return (
        <div
          style={{
            width: '100%',
            height: '32px',
            background: value,
            borderRadius: '6px',
            border: '1px solid #d9d9d9',
            marginTop: '8px'
          }}
        />
      );
    }
    return null;
  };

  return (
    <div style={style}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Radio.Group value={colorType} onChange={handleColorTypeChange}>
          <Radio value="solid">
            <Space>
              <BgColorsOutlined />
              <Text>单色</Text>
            </Space>
          </Radio>
          <Radio value="gradient">
            <Space>
              <BorderOutlined />
              <Text>渐变色</Text>
            </Space>
          </Radio>
        </Radio.Group>

        {colorType === 'solid' && (
          <ColorPicker
            format="hex"
            value={isGradient ? '#1890ff' : value}
            onChange={handleSolidColorChange}
            showText={showText}
            style={{ width: '100%' }}
          />
        )}

        {colorType === 'gradient' && (
          <div>
            <Button
              type="dashed"
              onClick={handleGradientModalOpen}
              style={{ width: '100%', height: '32px' }}
            >
              <Space>
                <BorderOutlined />
                <Text>选择渐变色模板</Text>
              </Space>
            </Button>
            {renderPreview()}
          </div>
        )}
      </Space>

      <Modal
        title="选择渐变色模板"
        open={gradientModalVisible}
        onCancel={() => setGradientModalVisible(false)}
        footer={null}
        width={800}
      >
        <GradientTemplateSelector
          onSelect={handleGradientSelect}
        />
      </Modal>
    </div>
  );
};

export default ColorGradientSelector;

